import { createFileRoute } from '@tanstack/react-router';
import { ChevronDownIcon, PlusIcon, SaveIcon, Trash2Icon } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Text } from '@/components/ui/text';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Group,
  Section,
} from '@/features/design-system/showcase-primitives';

export const Route = createFileRoute('/design-system/components')({
  component: ComponentsPage,
});

const BUTTON_VARIANTS = [
  'default',
  'outline',
  'secondary',
  'ghost',
  'destructive',
  'link',
] as const;

const BUTTON_SIZES = ['xs', 'sm', 'default', 'lg'] as const;
const BUTTON_ICON_SIZES = ['icon-xs', 'icon-sm', 'icon', 'icon-lg'] as const;

const BADGE_VARIANTS = [
  'default',
  'secondary',
  'destructive',
  'outline',
  'ghost',
  'link',
] as const;

function ComponentsPage() {
  return (
    <div className="flex flex-col gap-14">
      <header className="flex flex-col gap-2">
        <Text variant="eyebrow">Components</Text>
        <Text variant="display" as="h1">
          Live primitives
        </Text>
        <Text variant="body" tone="muted" className="max-w-2xl">
          Every block on this page renders the real source from{' '}
          <code className="font-mono">src/components/ui</code>. If a variant
          looks wrong here, it&apos;s wrong in production.
        </Text>
      </header>

      {/* ───── Buttons ───── */}
      <Section
        eyebrow="Components"
        title="Button"
        description="Six variants × four heights, plus four icon sizes. Default = solid near-black; outline / secondary / ghost cover everything else; destructive is bg-destructive/10 — never solid red."
      >
        <Group label="Variants × sizes">
          <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4">
            {BUTTON_VARIANTS.map((variant) => (
              <div
                key={variant}
                className="grid grid-cols-[7rem_1fr] items-center gap-4"
              >
                <Text variant="code" tone="muted">
                  {variant}
                </Text>
                <div className="flex flex-wrap items-center gap-2">
                  {BUTTON_SIZES.map((size) => (
                    <Button key={size} variant={variant} size={size}>
                      {size === 'xs'
                        ? 'XS'
                        : size === 'sm'
                          ? 'Small'
                          : size === 'lg'
                            ? 'Large'
                            : 'Default'}
                    </Button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Group>

        <Group label="Icon-only sizes">
          <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-card p-4">
            {BUTTON_ICON_SIZES.map((size) => (
              <Button key={size} variant="outline" size={size} aria-label="Save">
                <SaveIcon />
              </Button>
            ))}
            <span className="ms-2">
              <Text variant="caption">→ icon-xs · icon-sm · icon · icon-lg</Text>
            </span>
          </div>
        </Group>

        <Group label="States">
          <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-card p-4">
            <Button>Idle</Button>
            <Button disabled>Disabled</Button>
            <Button aria-invalid>aria-invalid</Button>
            <Button aria-expanded>aria-expanded</Button>
            <Button>
              <PlusIcon data-icon="inline-start" />
              New plan
            </Button>
            <Button variant="outline">
              Floor 2 of 3
              <ChevronDownIcon data-icon="inline-end" />
            </Button>
          </div>
        </Group>
      </Section>

      {/* ───── Badges ───── */}
      <Section
        eyebrow="Components"
        title="Badge"
        description="Pill-shaped (rounded-4xl), 20px tall, text-xs. Variants mirror Button. Used for branch counts, room totals, milestone labels."
      >
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-card p-4">
          {BADGE_VARIANTS.map((v) => (
            <Badge key={v} variant={v}>
              {v}
            </Badge>
          ))}
          <Badge>3</Badge>
          <Badge variant="secondary">12 rooms</Badge>
          <Badge variant="outline">Auto-checkpoint</Badge>
        </div>
      </Section>

      {/* ───── Cards ───── */}
      <Section
        eyebrow="Components"
        title="Card"
        description="rounded-xl, ring-1 ring-foreground/10, bg-card. Footer is special: bg-muted/50, border-t, padded — used to separate actions."
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Plan: Ground floor</CardTitle>
              <CardDescription>
                Last edited 2 minutes ago · 3 rooms · 18 placed items
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Text variant="caption">
                Cards use inner padding on Header/Content/Footer — never on the
                outer Card.
              </Text>
            </CardContent>
            <CardFooter className="justify-end gap-2">
              <Button variant="ghost" size="sm">
                Cancel
              </Button>
              <Button size="sm">Open</Button>
            </CardFooter>
          </Card>

          <Card size="sm">
            <CardHeader>
              <CardTitle>Compact card</CardTitle>
              <CardDescription>
                size=&quot;sm&quot; tightens vertical rhythm and reduces title
                size to text-sm.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Text variant="caption">
                Used in dense lists like the rooms sidebar and the rail panels.
              </Text>
            </CardContent>
          </Card>
        </div>
      </Section>

      {/* ───── Dialog ───── */}
      <Section
        eyebrow="Components"
        title="Dialog"
        description="bg-popover, ring-1, max-w-sm. Backdrop is bg-black/10 + a faint blur — not the heavy gauze you see in iOS."
      >
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-card p-4">
          <Dialog>
            <DialogTrigger render={<Button>Save milestone</Button>} />
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Save milestone</DialogTitle>
                <DialogDescription>
                  Snapshots the current state with a label you can find later.
                </DialogDescription>
              </DialogHeader>
              <div className="flex flex-col gap-2">
                <Label htmlFor="ds-milestone">Milestone name</Label>
                <Input id="ds-milestone" placeholder="e.g. Pre-electrician walkthrough" />
              </div>
              <DialogFooter showCloseButton>
                <Button>Save milestone</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog>
            <DialogTrigger
              render={<Button variant="destructive">Delete plan</Button>}
            />
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Delete plan?</DialogTitle>
                <DialogDescription>
                  This removes the plan and all its history. You can&apos;t
                  undo this.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter showCloseButton>
                <Button variant="destructive">
                  <Trash2Icon data-icon="inline-start" />
                  Delete
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </Section>

      {/* ───── Inputs ───── */}
      <Section
        eyebrow="Components"
        title="Inputs & forms"
        description="Inputs sit on a 1px border, transparent fill. Focus and invalid states stay quiet — 3px ring at low opacity, no fill."
      >
        <div className="grid grid-cols-1 gap-6 rounded-xl border border-border bg-card p-4 sm:grid-cols-2">
          <div className="flex flex-col gap-2">
            <Label htmlFor="ds-default">Plan name</Label>
            <Input id="ds-default" placeholder="Ground floor" />
            <Text variant="caption">Default state.</Text>
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="ds-focus">Focused (autofocused)</Label>
            <Input
              id="ds-focus"
              defaultValue="Pre-electrician walkthrough"
              autoFocus
            />
            <Text variant="caption">3px ring at 50% on the ring color.</Text>
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="ds-disabled">Disabled</Label>
            <Input id="ds-disabled" defaultValue="Read-only" disabled />
            <Text variant="caption">opacity-50, pointer-events-none.</Text>
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="ds-invalid">Invalid</Label>
            <Input
              id="ds-invalid"
              defaultValue="not-an-email"
              aria-invalid
            />
            <Text variant="body" tone="destructive">
              Enter a valid email address.
            </Text>
          </div>
          <div className="flex flex-col gap-2 sm:col-span-2">
            <Label htmlFor="ds-textarea">Notes for the electrician</Label>
            <Textarea
              id="ds-textarea"
              placeholder="Notes for the electrician…"
              rows={3}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="ds-select">Floor</Label>
            <Select defaultValue="ground">
              <SelectTrigger id="ds-select">
                <SelectValue placeholder="Pick a floor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="basement">Basement</SelectItem>
                <SelectItem value="ground">Ground floor</SelectItem>
                <SelectItem value="first">First floor</SelectItem>
                <SelectItem value="attic">Attic</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="ds-switch">Show grid</Label>
            <div className="flex items-center gap-2">
              <Switch id="ds-switch" defaultChecked />
              <Text variant="caption">Snaps placements to a 50mm grid.</Text>
            </div>
          </div>
        </div>
      </Section>

      {/* ───── Tooltip ───── */}
      <Section
        eyebrow="Components"
        title="Tooltip"
        description="Foreground-on-background with an arrow. delay=0 by default; surface only on focus or hover."
      >
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-card p-4">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger
                render={<Button variant="outline">Hover me</Button>}
              />
              <TooltipContent>Saved 2 seconds ago</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button variant="ghost" size="icon-sm" aria-label="Help">
                    <PlusIcon />
                  </Button>
                }
              />
              <TooltipContent>Add a Placed Item</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </Section>

      {/* ───── Sonner toaster ───── */}
      <Section
        eyebrow="Components"
        title="Toaster (Sonner)"
        description="Reserved for asynchronous events — collaboration, background jobs. Errors render inline near the failing control, never as global toasts."
      >
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-card p-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => toast.success('Saved milestone "Pre-walkthrough"')}
          >
            success
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => toast.info('Branch published 3 new changes')}
          >
            info
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => toast.warning('Auto-checkpoint paused — offline')}
          >
            warning
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => toast.error('Could not save the change.')}
          >
            error
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const id = toast.loading('Saving…');
              setTimeout(() => toast.success('Saved', { id }), 1500);
            }}
          >
            loading → success
          </Button>
        </div>
      </Section>

      {/* ───── Separator ───── */}
      <Section
        eyebrow="Components"
        title="Separator"
        description="1px hairline. Horizontal under headers; vertical between toolbar groups."
      >
        <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4">
          <Separator />
          <div className="flex h-8 items-center gap-3">
            <Text variant="body">Group A</Text>
            <Separator orientation="vertical" />
            <Text variant="body">Group B</Text>
            <Separator orientation="vertical" />
            <Text variant="body">Group C</Text>
          </div>
        </div>
      </Section>

      {/* ───── Item dot (visual reference) ───── */}
      <Section
        eyebrow="Domain"
        title="Placed Item dot"
        description="Drawn in Konva on the canvas, but the visual recipe is here. Inner fill = category color, single uppercase glyph in node-glyph color, slate stroke."
      >
        <div className="flex flex-wrap items-center gap-6 rounded-xl border border-border bg-card p-6">
          <ItemDot category="light" subtype="W" />
          <ItemDot category="light" subtype="C" />
          <ItemDot category="socket" subtype="F" />
          <ItemDot category="socket" subtype="U" />
          <ItemDot category="network" subtype="W" />
          <ItemDot category="network" subtype="A" />
          <ItemDot category="light" subtype="W" selected />
          <ItemDot category="socket" subtype="F" orphan />
        </div>
      </Section>
    </div>
  );
}

function ItemDot({
  category,
  subtype,
  selected,
  orphan,
}: {
  category: 'light' | 'socket' | 'network' | 'audio';
  subtype: string;
  selected?: boolean;
  orphan?: boolean;
}) {
  const fill = `var(--category-${category})`;
  return (
    <div className="relative flex flex-col items-center gap-2">
      {selected && (
        <span
          aria-hidden
          className="absolute top-0 left-1/2 size-12 -translate-x-1/2 -translate-y-1 rounded-full"
          style={{ background: 'var(--selection-halo)' }}
        />
      )}
      <span
        className="relative flex size-7 items-center justify-center rounded-full ring-1 ring-[var(--node-stroke)]"
        style={{
          background: fill,
          boxShadow: selected
            ? '0 0 0 3px var(--selection)'
            : orphan
              ? '0 0 0 2px var(--pulse-orphan)'
              : undefined,
        }}
      >
        <span
          className="text-[10px] font-semibold leading-none"
          style={{ color: 'var(--node-glyph)' }}
        >
          {subtype}
        </span>
      </span>
      <Text variant="caption">
        {category}/{subtype}
        {selected && ' · selected'}
        {orphan && ' · orphan'}
      </Text>
    </div>
  );
}
